
// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { supabase } from "@/integrations/supabase/client";
import type { Profile, QuickAccessItem } from "@/types/profile";

// Validate membership tier
export const validateMembershipTier = (tier: string): 'free' | 'Basic' | 'Advanced' | 'Professional' => {
  if (['free', 'Basic', 'Advanced', 'Professional'].includes(tier)) {
    return tier as 'free' | 'Basic' | 'Advanced' | 'Professional';
  }
  return 'free';
};

// Validate profile visibility
export const validateProfileVisibility = (visibility: string): 'public' | 'private' => {
  return visibility === 'public' ? 'public' : 'private';
};

// Validate user role
export const validateUserRole = (role: string): 'admin' | 'moderator' | 'user' => {
  if (['admin', 'moderator', 'user'].includes(role)) {
    return role as 'admin' | 'moderator' | 'user';
  }
  return 'user';
};

// Safely transform Supabase JSON to QuickAccessItems
export const parseQuickAccessItems = (data: unknown): QuickAccessItem[] | null => {
  if (!data) return null;
  return (data as unknown) as QuickAccessItem[];
};

// Fetch user profile data from Supabase
export const fetchUserProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .select(
      'id, first_name, last_name, display_name, phone, job_title, department, timezone, locale, bio, membership_tier, is_deleted, avatar_url, email_notifications, profile_visibility, quick_access_items, role, privacy_policy_version, privacy_policy_accepted_at, terms_of_service_version, terms_accepted_at, delivery_data_processing_consent_at'
    )
    .eq('id', userId)
    .single();

  if (error) throw error;
  return data;
};

// Format profile data from Supabase to our Profile type
export const formatProfileData = (profileData: any): Profile => {
  return {
    id: profileData.id,
    first_name: profileData.first_name,
    last_name: profileData.last_name,
    display_name: profileData.display_name ?? null,
    phone: profileData.phone ?? null,
    job_title: profileData.job_title ?? null,
    department: profileData.department ?? null,
    timezone: profileData.timezone ?? null,
    locale: profileData.locale ?? null,
    bio: profileData.bio ?? null,
    membership_tier: validateMembershipTier(profileData.membership_tier),
    avatar_url: profileData.avatar_url,
    email_notifications: profileData.email_notifications ?? true,
    profile_visibility: validateProfileVisibility(profileData.profile_visibility),
    quick_access_items: parseQuickAccessItems(profileData.quick_access_items),
    role: profileData.role ? validateUserRole(profileData.role) : 'user',
    privacy_policy_version: profileData.privacy_policy_version ?? null,
    privacy_policy_accepted_at: profileData.privacy_policy_accepted_at ?? null,
    terms_of_service_version: profileData.terms_of_service_version ?? null,
    terms_accepted_at: profileData.terms_accepted_at ?? null,
    delivery_data_processing_consent_at:
      profileData.delivery_data_processing_consent_at ?? null,
  };
};
